import { useCallback, useEffect, useRef, useState } from "react";
import { getBusinessWorkflows, updateBusinessWorkflow } from "../../businessModeService";
import { isTaskMantisDesktop } from "../../desktopUpdate";
import {
  fetchProviderAuthUrl,
  persistReconnectContext,
  subscribeBusinessOAuthPopupResult,
  toPopupOauthState,
} from "./businessDigestSetup";
import {
  findMeetNotesOffer,
  hasSeenMeetNotesTip,
  markMeetNotesTipSeen,
} from "./meetNotesTip";

const MEET_OAUTH_STATE = "business_google_meet";

export const MEET_NOTES_COPY = {
  offer:
    "For calls with no transcript, we read the notes Google Meet generated and extract your action items from them. Optional: it needs one-time Google Drive access to read Meet's own files.",
  opening: "Opening Google's permission screen…",
  waiting: "Finish in the Google window that just opened. We'll pick it up from there.",
  done: "Done. Gemini notes will now be used for calls that have no transcript.",
  error: "Something went wrong. You can try again.",
};

/**
 * Gemini-notes state for the What's new pane: whether the user has a Google Meet
 * connector at all, and the "Allow access" offer when that connector hasn't granted
 * Drive access and the offer wasn't dismissed.
 *
 * @example
 * const meet = useMeetNotesOffer("u-1");
 * // meet.hasMeetConnector: true for any Meet user; meet.meetWorkflowId opens its settings
 * // meet.workflow is null until the offer applies; meet.allow() opens Google's permission screen
 */
export const useMeetNotesOffer = (userId) => {
  const [workflow, setWorkflow] = useState(null);
  const [hasMeetConnector, setHasMeetConnector] = useState(false);
  // The Meet connector the settings link should open: the offered one, else the first.
  const [meetWorkflowId, setMeetWorkflowId] = useState("");
  const [phase, setPhase] = useState("offer"); // offer | opening | waiting | done | error
  const [errorText, setErrorText] = useState("");
  const unsubscribeRef = useRef(null);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const workflows = await getBusinessWorkflows();
        if (cancelled) return;
        const list = Array.isArray(workflows) ? workflows : [];
        const meetWorkflows = list.filter(
          (w) => String(w?.source || "").toLowerCase() === "google_meet" && w?.active !== false
        );
        setHasMeetConnector(meetWorkflows.length > 0);
        const offer = hasSeenMeetNotesTip(userId) ? null : findMeetNotesOffer(list);
        const target = offer || meetWorkflows[0];
        setMeetWorkflowId(String(target?.workflow_id || target?.connector_id || ""));
        if (offer) setWorkflow(offer);
      } catch (_) {
        // Not a Business-mode user or the list failed: nothing to offer.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(
    () => () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    },
    []
  );

  const dismiss = useCallback(() => {
    markMeetNotesTipSeen(userId);
    setWorkflow(null);
  }, [userId]);

  const allow = useCallback(async () => {
    if (!workflow) return;
    setPhase("opening");
    setErrorText("");
    try {
      // Asking for access means the user wants notes: record that first (best effort),
      // so a switch they earlier turned off in connector settings doesn't undo this.
      await updateBusinessWorkflow(workflow.workflow_id || workflow.connector_id, {
        use_gemini_notes: true,
      }).catch(() => {});
      // Re-authorise the existing connector's credential, not a new connector.
      persistReconnectContext({
        workflowId: workflow.workflow_id || workflow.connector_id,
        credentialId: workflow.credential_id || "",
      });
      const { authUrl } = await fetchProviderAuthUrl(
        "google_meet",
        toPopupOauthState(MEET_OAUTH_STATE)
      );
      const popup = window.open(authUrl, "_blank");
      // Electron hands Google's URL to the system browser, so window.open returns
      // null there by design; the code comes back through the app's OAuth deep
      // link (useDesktopOAuthLoopback) and is published like a popup result.
      if (!popup && !isTaskMantisDesktop()) {
        // Pop-ups blocked in a browser: continue in this tab; the Meet setup
        // page finishes the reconnect when Google sends the user back.
        markMeetNotesTipSeen(userId);
        const { authUrl: sameTabUrl } = await fetchProviderAuthUrl(
          "google_meet",
          MEET_OAUTH_STATE
        );
        window.location.assign(sameTabUrl);
        return;
      }
      setPhase("waiting");
      if (unsubscribeRef.current) unsubscribeRef.current();
      unsubscribeRef.current = subscribeBusinessOAuthPopupResult("google_meet", (result) => {
        if (result?.error) {
          setErrorText(String(result.error));
          setPhase("error");
        } else {
          markMeetNotesTipSeen(userId);
          setPhase("done");
        }
      });
    } catch (error) {
      setErrorText(String(error?.message || "Could not open Google's permission screen."));
      setPhase("error");
    }
  }, [workflow, userId]);

  return { workflow, hasMeetConnector, meetWorkflowId, phase, errorText, allow, dismiss };
};

